import { useState, useEffect, useRef } from 'react';
import type { ScheduleEvent, ConnectionStatus } from '@fulcrum/schedule-embedded';

interface SseScheduleOptions {
  url: string;
  enabled: boolean;
  onAddEvents: (events: ScheduleEvent[]) => void;
  onUpdateEvent: (event: ScheduleEvent) => void;
  onRemoveEvent: (eventId: string) => void;
}

/**
 * Manages an EventSource connection for live schedule updates.
 * The host remains in control of all data — this hook only drives the
 * EventSource lifecycle and forwards typed SSE messages to the provided
 * callbacks, keeping the embedded component transport-agnostic.
 *
 * Expected SSE event types from the server:
 *   add    — data: ScheduleEvent[]
 *   update — data: ScheduleEvent
 *   remove — data: { id: string }
 */
export function useSseSchedule({
  url,
  enabled,
  onAddEvents,
  onUpdateEvent,
  onRemoveEvent,
}: SseScheduleOptions): ConnectionStatus {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');

  // Keep callbacks in a ref so the effect never needs to re-run due to
  // inline function identity changes — the EventSource lifetime is driven
  // solely by `url` and `enabled`.
  const cbRef = useRef({ onAddEvents, onUpdateEvent, onRemoveEvent });
  useEffect(() => {
    cbRef.current = { onAddEvents, onUpdateEvent, onRemoveEvent };
  });

  useEffect(() => {
    if (!enabled) {
      setStatus('disconnected');
      return;
    }

    setStatus('connecting');
    const es = new EventSource(url);

    es.onopen = () => setStatus('connected');

    es.onerror = () => {
      // readyState CLOSED means the browser gave up retrying; CONNECTING means
      // it will retry automatically — reflect that distinction in the indicator.
      setStatus(es.readyState === EventSource.CLOSED ? 'error' : 'connecting');
    };

    es.addEventListener('add', (e: MessageEvent) => {
      try {
        cbRef.current.onAddEvents(JSON.parse(e.data) as ScheduleEvent[]);
      } catch { /* malformed frame — ignore */ }
    });

    es.addEventListener('update', (e: MessageEvent) => {
      try {
        cbRef.current.onUpdateEvent(JSON.parse(e.data) as ScheduleEvent);
      } catch { /* malformed frame — ignore */ }
    });

    es.addEventListener('remove', (e: MessageEvent) => {
      try {
        const { id } = JSON.parse(e.data) as { id: string };
        cbRef.current.onRemoveEvent(id);
      } catch { /* malformed frame — ignore */ }
    });

    return () => {
      es.close();
      setStatus('disconnected');
    };
  }, [url, enabled]);

  return status;
}
